<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useBookComposable } from '../useBookComposable';
import { cloneDeep } from 'lodash';
import { CRUISE } from '@/constants';
import Button from 'primevue/button';
import ColumnGroup from 'primevue/columngroup';
import DataTable from 'primevue/datatable';
import InputNumber from 'primevue/inputnumber';
import TableColumn from 'primevue/column';
import Row from 'primevue/row';
import DetailedOccupancy from '../DetailedOccupancy.vue';

const props = defineProps<{
  isManagement: boolean;
  priorityStatus: number | undefined;
  isShowAddPaxHotel?: boolean;
  hotelData?: any;
  itemData?: any;
  indexOption: number;
  indexHotel?: number;
  resultTotals: any;
  isShowDetailedOccupancy?: boolean;
}>();

const emits = defineEmits<{
  addPax: [data: any, index: number];
  updateBookingRequestForm: [event: any, room: any, indexOption: number, indexHotel: number, indexRoom: number];
  formatDataHotel: [rooms: any, hotel: string, indexOption: number];
  findRoomGetTotal: [indexOption: number, indexHotel: number, indexRoom: number, roomType: string, personType: string];
  onRowEditSaveHotel: [event: any, rooms: any, dataCommon: any, indexHotel: number];
  'update:isShowAddPaxHotel': [value: boolean];
}>();

const isShowAddPax = computed({
  get() {
    return props.isShowAddPaxHotel;
  },
  set(value) {
    emits('update:isShowAddPaxHotel', value);
  }
});

const { hasViewOccupacyPermission, hasEditBookPermission, checkPriorityStatus } = useBookComposable();

const editingRowsHotel = ref([]);
const listRoom = ref<any>([]);

const formatDataHotel = (rooms: any, hotel: string, indexOption: number) => {
  const data: any = [];
  rooms.forEach((room: any, indexRoom: number) => {
    const obj = {
      index: `${indexOption}${indexRoom}`,
      hotel: hotel,
      ...cloneDeep(room)
    };
    data.push(obj);
  });

  listRoom.value = cloneDeep(data);
};

const onRowEditSaveHotel = (event: any, rooms: any, dataCommon: any, indexHotel: number) => {
  emits('onRowEditSaveHotel', event, rooms, dataCommon, indexHotel);
};

const findRoomGetTotal = (indexOption: number, indexHotel: number, indexRoom: number, roomType: string, personType: string) => {
  if (props.resultTotals && props.resultTotals.length) {
    return props.resultTotals.find((item: any) => item.indexOption === indexOption && item.indexHotel === indexHotel && item.indexRoom === indexRoom)?.[roomType]?.[personType] ?? 0;
  }
};

const updateBookingRequestForm = (event: any, room: any, indexOption: number, indexHotel: number, indexRoom: number) => {
  emits('updateBookingRequestForm', event, room, indexOption, indexHotel, indexRoom);
};

const calculateTotalNoHotel = (hotelData: any, indexRoom: number) => {
  const roomInfo = hotelData.rooms[indexRoom].roomInfo;
  let total = 0;
  for (const key in roomInfo) {
    if (key.endsWith('No') && typeof roomInfo[key] === 'number') {
      total += roomInfo[key];
    }
  }
  if (total) {
    isShowAddPax.value = true;
  }
  return total;
};

const addPax = (data: any, index: number) => {
  emits('addPax', data, index);
};

watch(
  () => props.hotelData,
  () => {
    formatDataHotel(props.hotelData.rooms, props.hotelData.hotel, props.indexOption);
  },
  { immediate: true, deep: true }
);
</script>
<template>
  <div>
    <div>
      <DataTable
        :value="listRoom"
        rowGroupMode="rowspan"
        groupRowsBy="hotel"
        editMode="row"
        v-model:editingRows="editingRowsHotel"
        @row-edit-save="onRowEditSaveHotel($event, hotelData.rooms, itemData, indexHotel)"
        class="editable-cells-table bg-blue-50"
        responsiveLayout="stack"
      >
        <template #header>
          <p class="uppercase">{{ $t('common.hotel') }}: {{ hotelData.hotel }}</p>
        </template>
        <ColumnGroup type="header">
          <Row>
            <TableColumn field="room" :header="$t('book_form.room_category')" :rowspan="1" />
            <TableColumn v-if="hotelData.minPaxRoom <= 1 && hotelData.maxPaxRoom >= 1" :header="$t('book_form.single')" :colspan="isShowAddPax ? 3 : 1" />
            <TableColumn v-if="hotelData.minPaxRoom <= 2 && hotelData.maxPaxRoom >= 2" :header="$t('book_form.double')" :colspan="isShowAddPax ? 3 : 1" />
            <TableColumn v-if="hotelData.minPaxRoom <= 3 && hotelData.maxPaxRoom >= 3" :header="$t('book_form.triple')" :colspan="isShowAddPax ? 3 : 1" />
            <TableColumn v-if="hotelData.minPaxRoom <= 4 && hotelData.maxPaxRoom >= 4" :header="$t('book_form.quad')" :colspan="isShowAddPax ? 3 : 1" />
            <TableColumn v-if="hotelData.minPaxRoom <= 5 && hotelData.maxPaxRoom >= 5" :header="$t('book_form.five')" :colspan="isShowAddPax ? 3 : 1" />
            <TableColumn v-if="hotelData.minPaxRoom <= 6 && hotelData.maxPaxRoom >= 6" :header="$t('book_form.six')" :colspan="isShowAddPax ? 3 : 1" />
            <TableColumn :header="$t('common.edit')" :colspan="1" />
            <TableColumn :header="$t('common.action')" :colspan="1" />
          </Row>
          <Row v-if="isShowAddPax">
            <TableColumn :colspan="1" />
            <TableColumn v-if="hotelData.minPaxRoom <= 1 && hotelData.maxPaxRoom >= 1" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_rooms') }}</p>
              </template>
            </TableColumn>
            <TableColumn v-if="hotelData.minPaxRoom <= 1 && hotelData.maxPaxRoom >= 1" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_children') }}</p>
              </template>
            </TableColumn>
            <TableColumn v-if="hotelData.minPaxRoom <= 1 && hotelData.maxPaxRoom >= 1" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_infants') }}</p>
              </template>
            </TableColumn>

            <TableColumn v-if="hotelData.minPaxRoom <= 2 && hotelData.maxPaxRoom >= 2" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_rooms') }}</p>
              </template>
            </TableColumn>
            <TableColumn v-if="hotelData.minPaxRoom <= 2 && hotelData.maxPaxRoom >= 2" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_children') }}</p>
              </template>
            </TableColumn>
            <TableColumn v-if="hotelData.minPaxRoom <= 2 && hotelData.maxPaxRoom >= 2" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_infants') }}</p>
              </template>
            </TableColumn>

            <TableColumn v-if="hotelData.minPaxRoom <= 3 && hotelData.maxPaxRoom >= 3" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_rooms') }}</p>
              </template>
            </TableColumn>
            <TableColumn v-if="hotelData.minPaxRoom <= 3 && hotelData.maxPaxRoom >= 3" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_children') }}</p>
              </template>
            </TableColumn>
            <TableColumn v-if="hotelData.minPaxRoom <= 3 && hotelData.maxPaxRoom >= 3" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_infants') }}</p>
              </template>
            </TableColumn>

            <TableColumn v-if="hotelData.minPaxRoom <= 4 && hotelData.maxPaxRoom >= 4" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_rooms') }}</p>
              </template>
            </TableColumn>
            <TableColumn v-if="hotelData.minPaxRoom <= 4 && hotelData.maxPaxRoom >= 4" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_children') }}</p>
              </template>
            </TableColumn>
            <TableColumn v-if="hotelData.minPaxRoom <= 4 && hotelData.maxPaxRoom >= 4" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_infants') }}</p>
              </template>
            </TableColumn>

            <TableColumn v-if="hotelData.minPaxRoom <= 5 && hotelData.maxPaxRoom >= 5" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_rooms') }}</p>
              </template>
            </TableColumn>
            <TableColumn v-if="hotelData.minPaxRoom <= 5 && hotelData.maxPaxRoom >= 5" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_children') }}</p>
              </template>
            </TableColumn>
            <TableColumn v-if="hotelData.minPaxRoom <= 5 && hotelData.maxPaxRoom >= 5" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_infants') }}</p>
              </template>
            </TableColumn>

            <TableColumn v-if="hotelData.minPaxRoom <= 6 && hotelData.maxPaxRoom >= 6" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_rooms') }}</p>
              </template>
            </TableColumn>
            <TableColumn v-if="hotelData.minPaxRoom <= 6 && hotelData.maxPaxRoom >= 6" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_children') }}</p>
              </template>
            </TableColumn>
            <TableColumn v-if="hotelData.minPaxRoom <= 6 && hotelData.maxPaxRoom >= 6" class="width-column-group">
              <template #header>
                <p class="room-font-size">{{ $t('book_form.pound_sign_infants') }}</p>
              </template>
            </TableColumn>
            <TableColumn :colspan="1" />
            <TableColumn :colspan="1" />
          </Row>
        </ColumnGroup>
        <TableColumn field="room" :header="$t('book_form.room_category')" class="py-3"> </TableColumn>
        <TableColumn v-if="hotelData.minPaxRoom <= 1 && hotelData.maxPaxRoom >= 1" field="singleNo" :header="$t('book_form.single')">
          <template #editor="{ data, field }">
            <InputNumber :min="0" :max="1000" v-model="data.roomInfo[field]" :disabled="data.minPaxRoom > 1 || data.maxPaxRoom < 1" autofocus :useGrouping="false" />
          </template>
          <template #body="{ data, field }">
            <p>{{ data.roomInfo[field] || 0 }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="isShowAddPax && hotelData.minPaxRoom <= 1 && hotelData.maxPaxRoom >= 1" class="width-column-group">
          <template #body="{ index }">
            <p>{{ findRoomGetTotal(indexOption, indexHotel, index, 'singleRoom', 'children') }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="isShowAddPax && hotelData.minPaxRoom <= 1 && hotelData.maxPaxRoom >= 1" class="width-column-group">
          <template #body="{ index }">
            <p>{{ findRoomGetTotal(indexOption, indexHotel, index, 'singleRoom', 'infants') }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="hotelData.minPaxRoom <= 2 && hotelData.maxPaxRoom >= 2" field="doubleNo" :header="$t('book_form.double')">
          <template #editor="{ data, field }">
            <InputNumber :min="0" :max="1000" v-model="data.roomInfo[field]" :disabled="data.minPaxRoom > 2 || data.maxPaxRoom < 2" autofocus :useGrouping="false" />
          </template>
          <template #body="{ data, field }">
            <p>{{ data.roomInfo[field] || 0 }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="isShowAddPax && hotelData.minPaxRoom <= 2 && hotelData.maxPaxRoom >= 2" class="width-column-group">
          <template #body="{ index }">
            <p>{{ findRoomGetTotal(indexOption, indexHotel, index, 'doubleRoom', 'children') }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="isShowAddPax && hotelData.minPaxRoom <= 2 && hotelData.maxPaxRoom >= 2" class="width-column-group">
          <template #body="{ index }">
            <p>{{ findRoomGetTotal(indexOption, indexHotel, index, 'doubleRoom', 'infants') }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="hotelData.minPaxRoom <= 3 && hotelData.maxPaxRoom >= 3" field="tripleNo" :header="$t('book_form.triple')">
          <template #editor="{ data, field }">
            <InputNumber :min="0" :max="1000" v-model="data.roomInfo[field]" :disabled="data.minPaxRoom > 3 || data.maxPaxRoom < 3" autofocus :useGrouping="false" />
          </template>
          <template #body="{ data, field }">
            <p>{{ data.roomInfo[field] || 0 }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="isShowAddPax && hotelData.minPaxRoom <= 3 && hotelData.maxPaxRoom >= 3" class="width-column-group">
          <template #body="{ index }">
            <p>{{ findRoomGetTotal(indexOption, indexHotel, index, 'tripleRoom', 'children') }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="isShowAddPax && hotelData.minPaxRoom <= 3 && hotelData.maxPaxRoom >= 3" class="width-column-group">
          <template #body="{ index }">
            <p>{{ findRoomGetTotal(indexOption, indexHotel, index, 'tripleRoom', 'infants') }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="hotelData.minPaxRoom <= 4 && hotelData.maxPaxRoom >= 4" field="quadNo" :header="$t('book_form.quad')">
          <template #editor="{ data, field }">
            <InputNumber :min="0" :max="1000" v-model="data.roomInfo[field]" :disabled="data.minPaxRoom > 4 || data.maxPaxRoom < 4" autofocus :useGrouping="false" />
          </template>
          <template #body="{ data, field }">
            <p>{{ data.roomInfo[field] || 0 }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="isShowAddPax && hotelData.minPaxRoom <= 4 && hotelData.maxPaxRoom >= 4" class="width-column-group">
          <template #body="{ index }">
            <p>{{ findRoomGetTotal(indexOption, indexHotel, index, 'quadRoom', 'children') }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="isShowAddPax && hotelData.minPaxRoom <= 4 && hotelData.maxPaxRoom >= 4" class="width-column-group">
          <template #body="{ index }">
            <p>{{ findRoomGetTotal(indexOption, indexHotel, index, 'quadRoom', 'infants') }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="hotelData.minPaxRoom <= 5 && hotelData.maxPaxRoom >= 5" field="fiveInRoomNo" :header="$t('book_form.five')">
          <template #editor="{ data, field }">
            <InputNumber :min="0" :max="1000" v-model="data.roomInfo[field]" :disabled="data.minPaxRoom > 5 || data.maxPaxRoom < 5" autofocus :useGrouping="false" />
          </template>
          <template #body="{ data, field }">
            <p>{{ data.roomInfo[field] || 0 }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="isShowAddPax && hotelData.minPaxRoom <= 5 && hotelData.maxPaxRoom >= 5" class="width-column-group">
          <template #body="{ index }">
            <p>{{ findRoomGetTotal(indexOption, indexHotel, index, 'fiveRoom', 'children') }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="isShowAddPax && hotelData.minPaxRoom <= 5 && hotelData.maxPaxRoom >= 5" class="width-column-group">
          <template #body="{ index }">
            <p>{{ findRoomGetTotal(indexOption, indexHotel, index, 'fiveRoom', 'infants') }}</p>
          </template>
        </TableColumn>

        <TableColumn v-if="hotelData.minPaxRoom <= 6 && hotelData.maxPaxRoom >= 6" field="sixInRoomNo" :header="$t('book_form.six')">
          <template #editor="{ data, field }">
            <InputNumber :min="0" :max="1000" v-model="data.roomInfo[field]" :disabled="data.minPaxRoom > 6 || data.maxPaxRoom < 6" autofocus :useGrouping="false" />
          </template>
          <template #body="{ data, field }">
            <p>{{ data.roomInfo[field] || 0 }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="isShowAddPax && hotelData.minPaxRoom <= 6 && hotelData.maxPaxRoom >= 6" class="width-column-group">
          <template #body="{ index }">
            <p>{{ findRoomGetTotal(indexOption, indexHotel, index, 'sixRoom', 'children') }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="isShowAddPax && hotelData.minPaxRoom <= 6 && hotelData.maxPaxRoom >= 6" class="width-column-group">
          <template #body="{ index }">
            <p>{{ findRoomGetTotal(indexOption, indexHotel, index, 'sixRoom', 'infants') }}</p>
          </template>
        </TableColumn>
        <TableColumn v-if="hasEditBookPermission && !checkPriorityStatus(priorityStatus, isManagement)" :header="$t('common.edit')" :rowEditor="true" class="py-3"></TableColumn>
        <TableColumn v-if="hasViewOccupacyPermission" :header="$t('common.action')" class="py-3">
          <template #body="{ data, index }">
            <Button v-if="calculateTotalNoHotel(hotelData, index)" :label="isManagement ? $t('book_form.edit_pax') : $t('book_form.add_pax')" @click="addPax(data, index)" class="p-button-outlined add-pax-postHotel" />
          </template>
        </TableColumn>
      </DataTable>
      <div v-if="isShowDetailedOccupancy">
        <div v-for="(room, indexRoom) in hotelData.rooms" :key="indexRoom" class="mt-3">
          <DetailedOccupancy :qType="CRUISE" :hotel="hotelData" :room="room" :detailedOccupancy="room?.detailedOccupancy" @updateBookingRequestForm="updateBookingRequestForm($event, room, indexOption, indexHotel, indexRoom)"></DetailedOccupancy>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
:deep(.text-center .p-column-header-content) {
  justify-content: center;
}
.pre-line {
  white-space: pre-line;
}
</style>
